import { getFontCss, styleConstants } from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import AddAttachment from 'src/components/AddAttachment/AddAttachment'
import Dropzone from 'src/components/AddAttachment/Dropzone'
import AttachmentThumbnail from 'src/components/AttachmentThumbnail/AttachmentThumbnail'
import UploadingAttachment from 'src/components/UploadingAttachment/UploadingAttachment'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

interface AttachmentsListProps {
  className?: string
  attachments?: ReceiptAttachmentModel[]
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  uploadingAttachmentThumbnails?: Record<string, string>
  deleteAttachment?: (attachmentId: string) => void
  attachmentsText?: React.ReactElement
  isReceiptRequired?: boolean
  id?: string
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  errors?: boolean
  attachmentLimit?: number
  quickDeleteIcon?: boolean
  showSyncStatus?: boolean
  testId?: string
  showNewStyling?: boolean
  showVerificationBadges?: boolean
}

const AttachmentsList: React.FC<AttachmentsListProps> = (
  props,
): React.ReactElement => {
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const sortedAttachments = useMemo(
    (): ReceiptAttachmentModel[] | undefined =>
      props.attachments
        ? [...props.attachments].sort(
            (a, b) => moment(b.updatedAt).unix() - moment(a.updatedAt).unix(),
          )
        : undefined,
    [props.attachments],
  )

  const isAttachmentsListEmpty =
    sortedAttachments?.length === 0 &&
    props.uploadingAttachmentThumbnails &&
    Object.values(props.uploadingAttachmentThumbnails).length === 0

  return (
    <div
      id={props.id}
      data-testid={'attachmentsList'}
      data-count={props.attachments?.length || 0}
      css={css`
        display: flex;
        flex-wrap: wrap;
        ${props.attachments?.length === 0 &&
        props.uploadAttachments === undefined &&
        css`
          justify-content: center;
        `};
      `}
    >
      {props.uploadingAttachmentThumbnails !== undefined &&
        Object.entries(props.uploadingAttachmentThumbnails).map(
          ([uploadId, thumbnailUrl]) => (
            <UploadingAttachment
              key={uploadId}
              imgUrl={thumbnailUrl}
              data-testid={'uploadingAttachment'}
              css={css`
                margin-right: 10px;
                margin-bottom: 10px;
              `}
            />
          ),
        )}
      {sortedAttachments !== undefined &&
        sortedAttachments.map((att) => (
          <AttachmentThumbnail
            key={att.id}
            data-testid={'attachment'}
            attachment={att}
            deleteAttachment={props.deleteAttachment}
            isDeleting={false}
            errors={props.errors}
            setErrors={props.setErrors}
            quickDeleteIcon={props.quickDeleteIcon}
            syncedToERPStatus={
              props.showSyncStatus
                ? att.connectedPlatforms?.find(
                    (connection) =>
                      connection.platform ===
                      activeIntegration?.connectedPlatform,
                  )?.syncStatus
                : undefined
            }
            showVerificationBadge={props.showVerificationBadges}
            css={css`
              margin-right: 10px;
              margin-bottom: 10px;
              margin-left: 10px;
            `}
          />
        ))}
      {props.uploadAttachments !== undefined &&
        (props.showNewStyling ? (
          <Dropzone
            uploadAttachments={props.uploadAttachments}
            isAttachmentsListEmpty={isAttachmentsListEmpty}
            attachmentsText={props.attachmentsText}
            attachmentsLength={props.attachments?.length}
            isReceiptRequired={props.isReceiptRequired}
            setAttachmentError={props.setAttachmentError}
            setErrors={props.setErrors}
            attachmentLimit={props.attachmentLimit}
            testId="add-attachment"
            css={
              !isAttachmentsListEmpty
                ? css`
                    margin-right: 10px;
                    margin-bottom: 10px;
                  `
                : css``
            }
          />
        ) : (
          <AddAttachment
            uploadAttachments={props.uploadAttachments}
            isAttachmentsListEmpty={isAttachmentsListEmpty}
            attachmentsText={props.attachmentsText}
            attachmentsLength={props.attachments?.length}
            isReceiptRequired={props.isReceiptRequired}
            setAttachmentError={props.setAttachmentError}
            setErrors={props.setErrors}
            attachmentLimit={props.attachmentLimit}
            testId="add-attachment"
            css={
              !isAttachmentsListEmpty
                ? css`
                    margin-right: 10px;
                    margin-bottom: 10px;
                  `
                : css``
            }
          />
        ))}
      {props.attachments?.length === 0 &&
        props.uploadAttachments === undefined && (
          <span
            data-testid={'noAttachments'}
            css={css`
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.italic,
              )};
            `}
          >
            <Trns source={'No receipts have been uploaded'} />
          </span>
        )}
    </div>
  )
}

export default AttachmentsList
